import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Check, ArrowRight, Loader2, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { quizSubtitle } from "@/lib/quiz";
import { QuizResults } from "@/components/quiz/QuizResults";
import { ReportButton } from "@/components/quiz/ReportButton";
import { QuizImage, preloadImage } from "@/components/quiz/QuizImage";

const LETTERS = ["A", "B", "C", "D"];

async function localCorrectIndex(attemptId, index, key, count) {
  if (!key || !window.crypto?.subtle) return null;
  const enc = new TextEncoder();
  for (let i = 0; i < count; i++) {
    const buf = await crypto.subtle.digest("SHA-256", enc.encode(`${attemptId}:${index}:${i}`));
    const hex = Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
    if (hex.slice(0, 16) === key) return i;
  }
  return null;
}

function optionClass(i, chosen, correct) {
  if (chosen == null) return "border-[#27272A] bg-[#111113] hover:border-[#3F3F46] active:scale-[0.99]";
  if (i === correct) return "border-[#10B981] bg-[#10B981]/10";
  if (i === chosen) return "border-[#EF4444] bg-[#EF4444]/10";
  return "border-[#1F1F23] bg-[#0E0E10] opacity-50";
}

function Question({ q, chosen, onAnswer, attemptId, index, reported, xp }) {
  const answered = chosen != null && q.correct_index != null;
  const right = chosen === q.correct_index;
  return (
    <div className="fade-in">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#71717A] font-semibold truncate" data-testid="quiz-question-category">{q.category_label}</div>
        <ReportButton attemptId={attemptId} index={index} reported={reported} />
      </div>
      <QuizImage image={q.image} />
      <h2 className="text-xl font-semibold leading-snug mb-6" data-testid="quiz-question-text">{q.question}</h2>
      <div className="space-y-2.5" data-testid="quiz-options">
        {q.options.map((o, i) => (
          <button
            key={i}
            data-testid={`quiz-option-${i}`}
            disabled={chosen != null}
            onClick={() => onAnswer(i)}
            className={`w-full min-h-[60px] text-left px-4 py-3.5 rounded-2xl border flex items-center gap-3.5 transition-[border-color,background-color,opacity,transform] duration-150 ${optionClass(i, answered ? chosen : null, q.correct_index)} ${chosen === i && !answered ? "border-[#52525B]" : ""}`}
          >
            <span className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 ${answered && i === q.correct_index ? "bg-[#10B981] text-white" : answered && i === chosen ? "bg-[#EF4444] text-white" : "bg-[#222225] text-[#A1A1AA]"}`}>
              {answered && i === q.correct_index ? <Check size={14} strokeWidth={3} /> : answered && i === chosen ? <X size={14} strokeWidth={3} /> : LETTERS[i]}
            </span>
            <span className="text-[15px] leading-snug">{o}</span>
          </button>
        ))}
      </div>
      {answered && (
        <div className={`mt-5 p-4 rounded-2xl border-l-2 fade-in ${right ? "border-[#10B981] bg-[#10B981]/10" : "border-[#EF4444] bg-[#EF4444]/10"}`} data-testid="quiz-feedback">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className={`text-sm font-bold ${right ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid="quiz-feedback-verdict">{right ? "Correct" : "Not quite"}</div>
            {xp > 0 && <span className="flex items-center gap-1 text-xs font-bold text-[#E50914] fade-in" data-testid="quiz-feedback-xp"><Zap size={12} className="fill-[#E50914]" />+{xp} XP</span>}
          </div>
          {q.explanation ? <p className="text-sm text-[#D4D4D8] leading-relaxed fade-in">{q.explanation}</p> : <div className="space-y-2 pt-1"><div className="h-3 rounded bg-white/10 animate-pulse" /><div className="h-3 w-2/3 rounded bg-white/10 animate-pulse" /></div>}
        </div>
      )}
    </div>
  );
}

export default function QuizPlay() {
  const { id } = useParams();
  return <QuizPlayInner key={id} id={id} />;
}

function QuizPlayInner({ id }) {
  const { setUser } = useAuth();
  const [gained, setGained] = useState({});
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [answers, setAnswers] = useState(null);
  const [index, setIndex] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const inFlight = useRef(new Set());
  const { data: attempt, isLoading, isError, refetch } = useQuery({
    queryKey: ["quiz-attempt", id],
    queryFn: () => api.get(`/quizzes/attempts/${id}`).then((r) => r.data),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!attempt) return;
    setAnswers(attempt.answers);
    const first = attempt.answers.findIndex((a) => a == null);
    setIndex(first === -1 ? attempt.answers.length - 1 : first);
  }, [attempt?.id, attempt?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async () => {
    setFinishing(true);
    try {
      await Promise.allSettled([...inFlight.current]);
      const { data } = await api.post(`/quizzes/attempts/${id}/complete`);
      qc.setQueryData(["quiz-attempt", id], data);
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      qc.invalidateQueries({ queryKey: ["quiz-overview"] });
      qc.invalidateQueries({ queryKey: ["quiz-history"] });
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setFinishing(false);
    }
  };

  const onAnswer = async (choice) => {
    const prev = answers;
    const at = index;
    setAnswers(answers.map((a, i) => (i === at ? choice : a)));
    const setCachedAnswer = (v) => qc.setQueryData(["quiz-attempt", id], (old) => old && { ...old, answers: old.answers.map((a, i) => (i === at ? v : a)) });
    setCachedAnswer(choice);
    const q = attempt.questions[at];
    localCorrectIndex(id, at, q.key, q.options.length).then((ci) => {
      if (ci == null) return;
      qc.setQueryData(["quiz-attempt", id], (old) => old && {
        ...old,
        questions: old.questions.map((x, i) => (i === at && x.correct_index == null ? { ...x, correct_index: ci } : x)),
      });
    });
    try {
      const req = api.post(`/quizzes/attempts/${id}/answer`, { index: at, choice });
      inFlight.current.add(req);
      req.finally(() => inFlight.current.delete(req)).catch(() => {});
      const { data } = await req;
      qc.setQueryData(["quiz-attempt", id], (old) => old && {
        ...old,
        questions: old.questions.map((x, i) => (i === at ? { ...x, correct_index: data.correct_index, explanation: data.explanation } : x)),
      });
      if (data.xp) setGained((g) => ({ ...g, [at]: data.xp }));
      if (data.choice !== choice) {
        setAnswers((cur) => cur.map((a, i) => (i === at ? data.choice : a)));
        setCachedAnswer(data.choice);
      }
    } catch (err) {
      toast.error(errorMessage(err));
      setAnswers((cur) => cur.map((a, i) => (i === at ? prev[at] : a)));
      setCachedAnswer(prev[at]);
      qc.setQueryData(["quiz-attempt", id], (old) => old && {
        ...old,
        questions: old.questions.map((x, i) => (i === at && !x.explanation ? { ...x, correct_index: undefined } : x)),
      });
    }
  };

  const next = () => {
    if (index < answers.length - 1) {
      setIndex(index + 1);
      window.scrollTo(0, 0);
    } else finish();
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-md mx-auto min-h-screen bg-[#0A0A0A] px-5 pt-8 pb-32">{children}</div>
    </div>
  );

  useEffect(() => {
    attempt?.questions?.forEach((q) => preloadImage(q.image));
  }, [attempt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return shell(<div className="space-y-4 pt-10"><div className="h-6 w-1/2 rounded bg-[#161618] animate-pulse" /><div className="card h-72 animate-pulse" /></div>);
  if (isError || !attempt) {
    return shell(
      <div className="pt-16 text-center" data-testid="quiz-load-error">
        <p className="text-[#A1A1AA]">Couldn't load this quiz.</p>
        <div className="flex gap-2 mt-5"><button onClick={() => refetch()} className="btn-secondary">Try again</button><button onClick={() => navigate("/quizzes")} className="btn-secondary">Back</button></div>
      </div>
    );
  }

  if (attempt.status === "completed") {
    return shell(
      <>
        <button data-testid="quiz-close-button" onClick={() => navigate(-1)} className="mb-4 w-12 h-12 -ml-3 rounded-full flex items-center justify-center text-[#A1A1AA] hover:text-white active:bg-[#161618]" aria-label="Close"><X size={22} /></button>
        <QuizResults attempt={attempt} />
      </>
    );
  }

  if (attempt.status !== "in_progress") {
    return shell(
      <div className="pt-16 text-center" data-testid="quiz-discarded">
        <p className="text-[#A1A1AA]">This quiz was discarded.</p>
        <button onClick={() => navigate("/quizzes")} className="btn-primary mt-5">Choose a quiz</button>
      </div>
    );
  }

  if (!answers) return shell(null);
  const total = answers.length;
  const answeredCount = answers.filter((a) => a != null).length;
  const current = answers[index];

  return shell(
    <>
      <header className="flex items-center gap-3 mb-2">
        <button data-testid="quiz-close-button" onClick={() => navigate("/quizzes")} className="w-12 h-12 -ml-3 rounded-full flex items-center justify-center text-[#A1A1AA] hover:text-white active:bg-[#161618] shrink-0" aria-label="Save and exit"><X size={22} /></button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{attempt.title}</div>
          <div className="text-xs text-[#71717A] truncate">{quizSubtitle(attempt)}</div>
        </div>
        <span className="text-sm font-semibold tabular-nums shrink-0" data-testid="quiz-progress-text">{index + 1}/{total}</span>
      </header>
      <div className="flex gap-1 mb-8" data-testid="quiz-progress-bar">
        {answers.map((a, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-200 ${a == null ? (i === index ? "bg-white/40" : "bg-[#27272A]") : attempt.questions[i].correct_index == null ? "bg-white/40" : a === attempt.questions[i].correct_index ? "bg-[#10B981]" : "bg-[#EF4444]"}`} />
        ))}
      </div>

      <Question key={`${id}-${index}`} q={attempt.questions[index]} chosen={current} onAnswer={onAnswer} attemptId={id} index={index} reported={(attempt.reported || []).includes(index)} xp={gained[index]} />

      {current != null && (
        <div className="fixed bottom-0 inset-x-0 z-40 max-w-md mx-auto px-5 pt-3 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A] to-transparent safe-bottom" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}>
          <button data-testid="quiz-next-button" onClick={next} disabled={finishing} className="btn-primary">
            {finishing ? <Loader2 size={18} className="animate-spin" /> : index < total - 1 ? <>Next question <ArrowRight size={18} /></> : answeredCount === total ? <>See results <ArrowRight size={18} /></> : <>Next <ArrowRight size={18} /></>}
          </button>
        </div>
      )}
    </>
  );
}
